import { Color, Spacing } from '@persona-id/design-tokens';
import { useRef } from 'react';
import { COLORS, MEDIA_QUERIES } from 'shared/styles/variables';
import styled from 'styled-components';

import defaultTheme from 'design-system/theme';
import { ButtonType } from 'inquiry/components/Button';
import ButtonBase from 'inquiry/components/ButtonBase';
import ScannerOverlay from 'inquiry/components/ScannerOverlay';
import ScannerOverlayNew from 'inquiry/components/ScannerOverlay/ScannerOverlayNew';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { getCaptureFrameLabel } from 'lib/governmentId';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { GovernmentIdOverlay, GovernmentIdSide } from 'lib/interfaces/verificationConfig';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';
import ThemedTitle from 'verify/components/ThemedTitle';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import { StoredFileUpload } from 'verify/lib/interfaces/fileUploads';
import FlowType from 'verify/lib/interfaces/FlowType';
import { Z_INDICES } from 'verify/styles/variables';

/**
 * Value that gives a nice level of spacing between viewport and buttons
 * NOTE: default frame height is 650
 */
const VIDEO_WRAPPER_HEIGHT = 720;
const VideoWrapperFullscreen = styled('div')<{ flowType: FlowType }>`
  background: ${COLORS.black};
  overflow: hidden;
  position: relative;
  min-height: ${FRAME_HEIGHT}px;
  display: flex;
  justify-content: center;
  ${({ flowType }) =>
    // Don't use viewport units for inline flow
    flowType === FlowType.Inline
      ? `
      height: ${VIDEO_WRAPPER_HEIGHT}px;
    `
      : `
      @media ${MEDIA_QUERIES.isMobile} {
        height: 100%;
        min-height: 140vw;
      }
    `};

  ${HolderClient.isDashboardFLE && `height: 100%;`};
`;

const MAX_INLINE_COLUMN_WIDTH = 450;
const VideoWrapperColumnSizer = styled.div<{ restrictInlineColumnWidth: boolean }>`
  position: relative;
  width: 100%;
  max-width: ${({ restrictInlineColumnWidth }) =>
    restrictInlineColumnWidth ? `${MAX_INLINE_COLUMN_WIDTH}px` : null};
`;

const ButtonRowFullscreen = styled.div`
  width: 100%;
  position: absolute;
  bottom: ${Spacing[5]};
  display: flex;
  flex-direction: column;

  z-index: ${Z_INDICES.footer};
`;

const ButtonWrapper = styled.div`
  margin-bottom: ${Spacing[4]};
  margin-left: 10%;
  margin-right: 10%;
`;

const buttonThemeOverride = {
  ...defaultTheme,
  colorButtonPrimary: COLORS.white,
  colorFontButtonPrimary: COLORS.black,
  colorButtonSecondary: COLORS.white,
  colorFontButtonSecondary: COLORS.white,
  colorButtonSecondaryFill: COLORS.black,
  borderWidth: '1.5px',
  borderRadius: '24px',
};

const ThemedButton = styled(ButtonBase)``;

const TitlePositioner = styled.div`
  position: absolute;

  // matches default top margin on titles for other government id screen
  top: 72px;

  // matches margins of capture window
  left: 10%;
  right: 10%;

  // ignore SST colors and always white text color
  * {
    color: ${Color.PrimaryWhite} !important;
  }
  z-index: ${Z_INDICES.navbar};
`;

const CapturedImage = styled('img')`
  // ensure image stays centered as div shrinks during animation
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

interface InquiryGovernmentIdCheckViewRedesignProps {
  // check page in redesign view should use same assets as capture page since it uses the same overlay
  assets: StepGovernmentIdConfig['assets']['capture-page'];
  currentUpload: StoredFileUpload;
  isProcessing: boolean;
  localizations: StepGovernmentIdConfig['localizations']['check-page'];
  handleContinue: () => void;
  handleRetry: () => void;
  governmentIdClass: GovernmentIdClass;
  requiresSide: GovernmentIdSide;
  captureOverlay?: GovernmentIdOverlay;
  reverseSide: GovernmentIdSide;
  collapsed: boolean;
  flipped: boolean;
  featureFlags: FeatureFlagsMap;
  uploadProgress?: number;
}

export default function InquiryGovernmentIdCheckViewRedesign(
  props: InquiryGovernmentIdCheckViewRedesignProps,
) {
  const {
    assets,
    requiresSide,
    governmentIdClass,
    captureOverlay,
    currentUpload,
    localizations,
    handleContinue,
    handleRetry,
    reverseSide,
    collapsed,
    flipped,
    isProcessing,
    featureFlags,
    uploadProgress,
  } = props;

  const {
    base: baseStyles,
    reviewImageBox,
    captureOverlayHeaderButton,
    captureHint,
    captureHintIcon,
    captureButtons,
  } = useStepGovernmentIdStyleContext();

  const isShortBarcodeOverlay = requiresSide === GovernmentIdSide.BarcodePdf417;

  const useNewPerfLayout =
    featureFlags['inquiry:enable-experimental-govid-perf-improvements'] &&
    featureFlags['inquiry:enable-experimental-govid-perf-improvements-percentage'];

  const previewViewfinderRef = useRef<HTMLDivElement>();

  // if title is present there is no room to show the prompt, so hide it
  const prompt = localizations['title-confirm-capture'] ? null : localizations['prompt'];

  return (
    <>
      <VideoWrapperFullscreen flowType={HolderClient.flowType}>
        <VideoWrapperColumnSizer restrictInlineColumnWidth={HolderClient.isInline}>
          {localizations['title-confirm-capture'] ? (
            <TitlePositioner>
              <ThemedTitle styles={baseStyles?.titleStyle}>
                {localizations['title-confirm-capture']}
              </ThemedTitle>
            </TitlePositioner>
          ) : null}
          {useNewPerfLayout ? (
            <ScannerOverlayNew
              assets={assets}
              styles={{
                base: baseStyles,
                captureBox: reviewImageBox,
                captureOverlayHeaderButton: captureOverlayHeaderButton,
                hint: captureHint?.textStyle,
              }}
              governmentIdClass={governmentIdClass}
              requiresSide={requiresSide}
              captureOverlay={captureOverlay}
              captureFrameLabel={getCaptureFrameLabel(
                localizations,
                governmentIdClass,
                requiresSide,
              )}
              reverseCaptureFrameLabel={getCaptureFrameLabel(
                localizations,
                governmentIdClass,
                reverseSide,
              )}
              prompt={prompt}
              reverseSide={reverseSide}
              collapsed={collapsed}
              flipped={flipped}
              capturing={true}
              renderCaptureWindowContents={() => (
                <CapturedImage
                  alt={getCaptureFrameLabel(localizations, governmentIdClass, requiresSide)}
                  data-test='scanner-overlay__captured-image'
                  src={currentUpload.dataUrl}
                />
              )}
              isShortBarcodeOverlay={isShortBarcodeOverlay}
              isAnimated={true}
            />
          ) : (
            <ScannerOverlay
              assets={assets}
              styles={{
                base: baseStyles,
                captureBox: reviewImageBox,
                captureHintIcon,
                captureOverlayHeaderButton: captureOverlayHeaderButton,
                hint: captureHint?.textStyle,
              }}
              governmentIdClass={governmentIdClass}
              requiresSide={requiresSide}
              captureOverlay={captureOverlay}
              captureFrameLabel={getCaptureFrameLabel(
                localizations,
                governmentIdClass,
                requiresSide,
              )}
              reverseCaptureFrameLabel={getCaptureFrameLabel(
                localizations,
                governmentIdClass,
                reverseSide,
              )}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              prompt={prompt}
              reverseSide={reverseSide}
              collapsed={collapsed}
              flipped={flipped}
              capturing={true}
              capturedImageUrl={currentUpload.dataUrl}
              isShortBarcodeOverlay={isShortBarcodeOverlay}
              previewViewfinderRef={previewViewfinderRef}
              videoReady={true}
            />
          )}

          {!collapsed && (
            <ButtonRowFullscreen>
              <ButtonWrapper>
                <ThemedButton
                  buttonType={ButtonType.Primary}
                  theme={buttonThemeOverride}
                  disabled={isProcessing}
                  styles={captureButtons?.submit}
                  id='government_id__check__continue'
                  onClick={handleContinue}
                  data-test='government-id-check__continue'
                  isPending={isProcessing}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  uploadProgress={
                    isProcessing && uploadProgress ? Math.round(uploadProgress * 100) : null
                  }
                  pendingContent={localizations.uploading}
                >
                  {localizations['btn-submit']}
                </ThemedButton>
              </ButtonWrapper>
              <ButtonWrapper>
                <ThemedButton
                  buttonType={ButtonType.Secondary}
                  styles={captureButtons?.retake}
                  theme={buttonThemeOverride}
                  disabled={isProcessing}
                  id='government_id__retake'
                  onClick={handleRetry}
                  data-test='government-id-check__retake'
                >
                  {localizations['btn-retake']}
                </ThemedButton>
              </ButtonWrapper>
            </ButtonRowFullscreen>
          )}
        </VideoWrapperColumnSizer>
      </VideoWrapperFullscreen>
    </>
  );
}
